import * as React from 'react';
import {Helmet} from 'react-helmet';
import {useSelector} from 'react-redux';
import {Route, Switch, withRouter, Redirect} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {Session} from 'nutshell-core/session';
import type {UserPermissions} from 'nutshell-core/users/users-types';

import {CTAVideoBanner} from 'shells/cta-banner/cta-video-banner';
import {headTitle} from 'shells/document/head-title';
import {Link} from 'shells/link/link';

import {analyticsClient} from '../../analytics-client';
import {setBodyId} from '../../dom';
import type {NutshellState} from '../../store';

import {Setting} from '../core/setting';
import {AnalyticsStatusButton} from '../email-marketing/analytics/report/analytics-status/analytics-status-button';
import {mountFormFlashBanner} from '../forms/form-flash-banner/mount-form-flash-banner';
import {IntercomLaunchButton, launchIntercomMessenger} from '../intercom';
import {PageWrapper} from '../layout/page-wrapper';
import {ActivityQuotaFeatureGate} from '../limits/feature-gates/activity-quota-feature-gate';
import {MarketsFeatureGate} from '../limits/feature-gates/markets-feature-gate';
import {SalesQuotaFeatureGate} from '../limits/feature-gates/sales-quota-feature-gate';
import {TerritoriesFeatureGate} from '../limits/feature-gates/territories-feature-gate';
import {TitleBarLimits} from '../limits/title-bar-limits';
import {SalesAutomationRoot} from '../sales-automation/sales-automation-root';
import {TrashPage} from '../trash/trash-page';

import {ActivityTypes} from './activity-types';
import {AiFeaturesPage} from './ai-features/ai-features-page';
import {AiFeaturesPageV2} from './ai-features/ai-features-page-v2';
import {GetAPISupport} from './api-support/get-api-support';
import {AuditLogWrapper} from './audit-log/audit-log-wrapper';
import {BillingRoot} from './billing/billing-root';
import {CustomFieldsRoot} from './custom-fields/custom-fields-root';
import {DomainsPage} from './domains/domains-page';
import {
    userCanAccessSetupPage,
    getSetupSidebar,
    getFirstAccessibleSetupPathForUser,
    getBestDashboardRedirectPath,
    getSetupPageHeader,
} from './helpers';
import {ImportRoot} from './import/import-root';
import {ConstantContactPage} from './integrations/constant-contact/constant-contact-page';
import {SqlAccessPage} from './integrations/sql-access/sql-access-page';
import {IntegrationsNewRoot} from './integrations-new/integrations-new-root';
import {mountSettingsSidebar} from './mount-settings-sidebar';
import {mountTerritoriesBanner} from './mount-territories-banner';
import {PeopleIqPage} from './people-iq/people-iq-page';
import {ProductsPageActionBttns} from './products/products-page-action-bttns';
import {QuoteTemplateRoot} from './sales-documents/quotes/quote-template-root';
import {SecurityPage} from './security/security-page';
import {SetupPage} from './setup-page';
import {mountSetupSources} from './sources/mount-setup-sources';
import {SourcesPageDescription} from './sources/sources-page-description';
import {TagsRoot} from './tags/tags-root';
import {TrackingSnippetPage} from './tracking-snippet/tracking-snippet-page';
import type {TitleBarProps} from './types';
import {UserRoot} from './user/user-root';
import 'less/integration.less';
import 'less/my-account.less';
import 'less/setup-activity-benchmark.less';
import 'less/setup-billing.less';
import 'less/setup-builder.less';
import 'less/setup-distribution.less';
import 'less/setup-import.less';
import 'less/setup-index.less';
import 'less/setup-security.less';
import 'less/setup-integration.less';
import 'less/setup-process-builder.less';
import 'less/setup-quota.less';
import 'less/setup-region.less';
import 'less/setup-sales-automation.less';
import 'less/setup-segmented-arrows.less';
import 'less/setup-sender-address.less';
import 'less/setup-stage.less';
import 'less/setup-territories.less';
import 'less/setup-unbounce.less';
import 'less/setup.less';
import 'less/filepicker-modal.less';

export type ServerTitleBarProps = {
    pageTitle?: string;
    pageSubtitle?: string;
};

export function SetupRootComponent(props: ContextRouter) {
    const pathname = props.location.pathname; // location is passed in by withRouter
    const titles = getPageTitles(pathname);
    const bodyElem = document.querySelector('body');
    const isPaused = Boolean(bodyElem && bodyElem.dataset.isInstancePaused);
    const isDisabled = Boolean(bodyElem && bodyElem.dataset.isInstanceDisabled);
    const [serverTitleBarValues, setServerTitleBarValues] = React.useState<
        ServerTitleBarProps | null | undefined
    >(null);

    const userPermissions = useSelector((state: NutshellState) =>
        Session.getUserPermissions(state)
    );

    // If the user cannot access the setup page we are trying to hit, redirect
    // to the first accessible setup page, otherwise redirect to the dashboard
    if (!userCanAccessSetupPage(userPermissions, pathname)) {
        return (
            <Redirect
                exact={true}
                from={pathname}
                to={
                    getFirstAccessibleSetupPathForUser(userPermissions) ||
                    getBestDashboardRedirectPath(userPermissions) ||
                    ''
                }
            />
        );
    }

    return (
        <PageWrapper
            header={getSetupPageHeader(pathname, titles, serverTitleBarValues)}
            sidebar={getSetupSidebar(pathname, isPaused, isDisabled)}
        >
            <Switch>
                <Route
                    path={[
                        '/setup/outcome',
                        '/setup/stage',
                        '/setup/distribution',
                        '/setup/process-builder',
                        '/setup/delay',
                    ]}
                >
                    <Setting settingName='hide_sales_automation_upgrade_banner_NUT-12184'>
                        {/* @ts-expect-error */}
                        {({isLoading, setting, updateSetting}) => {
                            if (
                                // Need this check because /setup/outcome exists is new SA
                                !NutClientConfig.hasSalesProcessSetupOverhaulNUT7581 &&
                                !isLoading &&
                                setting &&
                                !setting.value
                            ) {
                                return (
                                    <div style={{margin: '16px 16px 0 16px'}}>
                                        <CTAVideoBanner
                                            analyticsClient={analyticsClient}
                                            // @ts-expect-error
                                            videoLink='https://youtu.be/9nPLL5bzIsI'
                                            title='Introducing Nutshell Sales Automation 2.0!'
                                            text='We’ve overhauled our sales automation capabilities and added email automation – update for free now!'
                                            buttonText='Contact support to learn more'
                                            thumbnailImageSource='/include/images/sales-automation/upgrade-cta.jpg'
                                            videoId='9nPLL5bzIsI'
                                            onDismiss={() => {
                                                updateSetting('1');
                                            }}
                                            onPrimaryAction={launchIntercomMessenger}
                                        />
                                    </div>
                                );
                            }

                            return null;
                        }}
                    </Setting>
                </Route>
                <Route>
                    <div />
                </Route>
            </Switch>
            <Switch>
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/index'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('settings');

                        return (
                            <SetupPage
                                pageTitle='Settings'
                                viewUrl={routeProps.location.pathname}
                                viewParams={routeProps.location.search}
                                onViewRendered={() => {
                                    mountSettingsSidebar();
                                }}
                            />
                        );
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/security'
                    render={() => {
                        return <SecurityPage />;
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/user'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('users');

                        return <UserRoot pathname={routeProps.location.pathname} />;
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/email-security'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('list-email-security');

                        return (
                            <SetupPage
                                pageTitle='Email security'
                                viewUrl={routeProps.location.pathname}
                                viewParams={routeProps.location.search}
                                onViewRendered={mountSettingsSidebar}
                            />
                        );
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/ai-features'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('list-ai-features');

                        return (
                            <div className='pad-16'>
                                {NutClientConfig.hasAiOutcomesFF ? (
                                    <AiFeaturesPageV2 />
                                ) : (
                                    <AiFeaturesPage />
                                )}
                            </div>
                        );
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/domains'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('list-domains');

                        return (
                            <div className='pad-16'>
                                <DomainsPage />
                            </div>
                        );
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/tracking-snippet'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('list-tracking-snippet');

                        return (
                            <div className='pad-16'>
                                <TrackingSnippetPage />
                            </div>
                        );
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/sender-address'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('list-sender-address');

                        return (
                            <SetupPage
                                pageTitle='Sender addresses'
                                viewUrl={routeProps.location.pathname}
                                viewParams={routeProps.location.search}
                                onViewRendered={mountSettingsSidebar}
                            />
                        );
                    }}
                />
                <Route path='/setup/billing' component={BillingRoot} />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/sales-automation'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('sales-automation');

                        return <SalesAutomationRoot />;
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/outcome'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('list-outcome');

                        return (
                            <SetupPage
                                pageTitle='Outcomes'
                                viewUrl={routeProps.location.pathname}
                                viewParams={routeProps.location.search}
                                onViewRendered={mountSettingsSidebar}
                            />
                        );
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/stage'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('list-stage');

                        return (
                            <SetupPage
                                pageTitle='Stages'
                                viewUrl={routeProps.location.pathname}
                                viewParams={routeProps.location.search}
                            />
                        );
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/distribution'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('');

                        const pageTitle = routeProps.location.pathname.includes('user')
                            ? 'Assignment'
                            : 'Triggers';

                        return (
                            <SetupPage
                                pageTitle={pageTitle}
                                viewUrl={routeProps.location.pathname}
                                viewParams={routeProps.location.search}
                            />
                        );
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/process-builder'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('process-builder');

                        return (
                            <SetupPage
                                pageTitle='Processes'
                                viewUrl={routeProps.location.pathname}
                                viewParams={routeProps.location.search}
                            />
                        );
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/delay'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('list-delay');

                        return (
                            <SetupPage
                                pageTitle='Delays'
                                viewUrl={routeProps.location.pathname}
                                viewParams={routeProps.location.search}
                            />
                        );
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/company-type'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('list-company-type');

                        return (
                            <SetupPage
                                pageTitle='Company types'
                                viewUrl={routeProps.location.pathname}
                                viewParams={routeProps.location.search}
                                onViewRendered={mountSettingsSidebar}
                            />
                        );
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/competitor'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('list-competitor');

                        return (
                            <SetupPage
                                pageTitle='Competitors'
                                viewUrl={routeProps.location.pathname}
                                viewParams={routeProps.location.search}
                                onViewRendered={mountSettingsSidebar}
                            />
                        );
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/custom-field-old'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('list-custom-fields');

                        return (
                            <SetupPage
                                pageTitle='Custom Fields'
                                viewUrl={routeProps.location.pathname}
                                viewParams={routeProps.location.search}
                                onViewRendered={mountSettingsSidebar}
                            />
                        );
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/custom-field'
                    render={CustomFieldsRoot}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/quote-template'
                    render={QuoteTemplateRoot}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/activity-type'
                    render={ActivityTypes}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/industry'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('list-industry');

                        return (
                            <SetupPage
                                pageTitle='Industries'
                                viewUrl={routeProps.location.pathname}
                                viewParams={routeProps.location.search}
                                onViewRendered={mountSettingsSidebar}
                            />
                        );
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/market'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('list-market');
                        if (!NutClientConfig.hasMultipleMarkets) {
                            return <MarketsFeatureGate />;
                        }

                        return (
                            <SetupPage
                                pageTitle='Markets'
                                viewUrl={routeProps.location.pathname}
                                viewParams={routeProps.location.search}
                                onViewRendered={mountSettingsSidebar}
                            />
                        );
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/product'
                    render={() => {
                        return <Redirect to='/sales/product' />;
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/source'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('list-source');

                        return (
                            <SetupPage
                                pageTitle='Sources'
                                viewUrl={routeProps.location.pathname}
                                viewParams={routeProps.location.search}
                                onViewRendered={() => {
                                    mountSetupSources();
                                    mountSettingsSidebar();
                                }}
                            />
                        );
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/tag'
                    render={(routeProps) => {
                        if (NutClientConfig.hasColoredTags) {
                            return <TagsRoot />;
                        }

                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('list-tag');

                        return (
                            <SetupPage
                                pageTitle='Tags'
                                viewUrl={routeProps.location.pathname}
                                viewParams={routeProps.location.search}
                                onViewRendered={mountSettingsSidebar}
                            />
                        );
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/territory'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('');

                        if (!NutClientConfig.hasTerritories) {
                            return <TerritoriesFeatureGate />;
                        }

                        return (
                            <SetupPage
                                pageTitle='Territories'
                                viewUrl={routeProps.location.pathname}
                                viewParams={routeProps.location.search}
                                onViewRendered={() => {
                                    mountSettingsSidebar();

                                    mountTerritoriesBanner();
                                }}
                            />
                        );
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/quota'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('quota-manager');

                        if (!NutClientConfig.hasQuotas) {
                            return <SalesQuotaFeatureGate />;
                        }

                        return (
                            <SetupPage
                                pageTitle='Sales quotas'
                                viewUrl={routeProps.location.pathname}
                                viewParams={routeProps.location.search}
                                onViewRendered={mountSettingsSidebar}
                            />
                        );
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/activity-quota/:action?'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('');

                        const isNew = routeProps.match.params.action === 'new';
                        if (!NutClientConfig.hasQuotas) {
                            return <ActivityQuotaFeatureGate />;
                        }

                        return (
                            <SetupPage
                                pageTitle='Activity quotas'
                                viewUrl={routeProps.location.pathname}
                                viewParams={routeProps.location.search}
                                onViewRendered={() => {
                                    mountSettingsSidebar();

                                    if (isNew) {
                                        const editButton =
                                            document.querySelector('#role-name .action-edit');
                                        if (editButton) {
                                            setTimeout(() => {
                                                // @ts-expect-error
                                                editButton.click();
                                            }, 0);
                                        }
                                    }
                                }}
                            />
                        );
                    }}
                />
                <Redirect exact={true} from='/setup/instant-integration' to='/setup/integration' />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/integration'
                    render={(routeProps) => {
                        return <IntegrationsNewRoot {...routeProps} />;
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/destination'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('list-destination');

                        return (
                            <SetupPage
                                pageTitle='Destinations'
                                viewUrl={routeProps.location.pathname}
                                viewParams={routeProps.location.search}
                            />
                        );
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/api-key'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('list-API-key');

                        return (
                            <SetupPage
                                pageTitle='API Keys'
                                viewUrl={routeProps.location.pathname}
                                viewParams={routeProps.location.search}
                                onViewRendered={mountSettingsSidebar}
                            />
                        );
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/mobile'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('list-mobile');

                        return (
                            <SetupPage
                                pageTitle='Mobiles'
                                viewUrl={routeProps.location.pathname}
                                viewParams={routeProps.location.search}
                            />
                        );
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/unbounce'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('');

                        return (
                            <SetupPage
                                pageTitle='Unbounce Pages'
                                viewUrl={routeProps.location.pathname}
                                viewParams={routeProps.location.search}
                                onViewRendered={() => {
                                    SetupUnbounce.loadIntegrationStatusesAsync();
                                    mountFormFlashBanner();
                                }}
                            />
                        );
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/sql-access'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('');

                        return (
                            <>
                                <Helmet>
                                    <title>{headTitle('SQL Access')}</title>
                                </Helmet>
                                <SqlAccessPage />
                            </>
                        );
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/constant-contact'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('');

                        return (
                            <React.Fragment>
                                <Helmet>
                                    <title>{headTitle('Constant Contact')}</title>
                                </Helmet>
                                <ConstantContactPage />
                            </React.Fragment>
                        );
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/(import.*)'
                    render={(routeProps: ContextRouter) => {
                        return (
                            <ImportRoot
                                {...routeProps}
                                onServerTitleBarChange={setServerTitleBarValues}
                            />
                        );
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/export'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('list-export');

                        return (
                            <SetupPage
                                pageTitle='Exports'
                                viewUrl={routeProps.location.pathname}
                                viewParams={routeProps.location.search}
                                onViewRendered={mountSettingsSidebar}
                            />
                        );
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/people-iq'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('');

                        return <PeopleIqPage />;
                    }}
                />
                <Redirect exact={true} from='/setup/audit-log' to='/setup/audit-log/bulk-edits' />
                <Redirect
                    exact={true}
                    from='/setup/audit-log/exports'
                    to='/setup/audit-log/list-actions'
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/setup/audit-log/:tab'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('');

                        return <AuditLogWrapper pageTitle='Audit log' {...routeProps} />;
                    }}
                />
                <SetupRoute
                    userPermissions={userPermissions}
                    path='/trash'
                    render={(routeProps) => {
                        setBodyClasses(routeProps.location.pathname);
                        setBodyId('trash');

                        return (
                            <React.Fragment>
                                <Helmet>
                                    <title>{headTitle('Trash')}</title>
                                </Helmet>
                                <TrashPage />
                            </React.Fragment>
                        );
                    }}
                />
            </Switch>
        </PageWrapper>
    );
}

const SetupRoute = ({
    path,
    render,
    userPermissions,
}: {
    path: string;
    render: (routeProps: ContextRouter) => React.ReactElement;
    userPermissions: UserPermissions | null | undefined;
}) => {
    if (userCanAccessSetupPage(userPermissions, path)) {
        return <Route path={path} render={render} />;
    } else {
        return <Redirect to='/dashboard' />;
    }
};

const baseBodyClasses = ['manage', 'page', 'settings'];

function setBodyClasses(pathname: string): void {
    const body = document.querySelector('body');

    if (!body) return;

    if (pathname.includes('/setup/index')) {
        body.className = baseBodyClasses.concat('list').join(' ');
    } else if (pathname.includes('/setup/outcome')) {
        body.className = baseBodyClasses.concat('list').join(' ');
    } else if (pathname.includes('/setup/distribution/overlay')) {
        body.className = baseBodyClasses
            .concat('list', 'distribution', 'distribution-overlay')
            .join(' ');
    } else if (pathname.includes('/setup/distribution')) {
        body.className = baseBodyClasses.concat('list', 'distribution', 'fixed-width').join(' ');
    } else if (pathname.includes('/setup/process-builder')) {
        body.className = baseBodyClasses.concat('list', 'fixed-width').join(' ');
    } else if (pathname.includes('/setup/delay')) {
        body.className = baseBodyClasses.concat('list').join(' ');
    } else if (pathname.includes('/setup/stage')) {
        body.className = baseBodyClasses.concat('list', 'fixed-width').join(' ');
    } else if (pathname.includes('/setup/activity-type')) {
        body.className = baseBodyClasses.concat('list').join(' ');
    } else if (pathname.includes('/setup/company-type')) {
        body.className = baseBodyClasses.concat('list').join(' ');
    } else if (pathname.includes('/setup/competitor')) {
        body.className = baseBodyClasses.concat('list').join(' ');
    } else if (pathname.includes('/setup/custom-field')) {
        body.className = baseBodyClasses.concat('list').join(' ');
    } else if (pathname.includes('/setup/export')) {
        body.className = baseBodyClasses.concat('list').join(' ');
    } else if (pathname.includes('/setup/industry')) {
        body.className = baseBodyClasses.concat('list').join(' ');
    } else if (pathname.includes('/setup/market')) {
        body.className = baseBodyClasses.concat('list').join(' ');
    } else if (pathname.includes('/setup/product')) {
        body.className = baseBodyClasses.concat('list').join(' ');
    } else if (pathname.includes('/setup/source')) {
        body.className = baseBodyClasses.concat('list').join(' ');
    } else if (pathname.includes('/setup/tag')) {
        body.className = baseBodyClasses.concat('list', 'fixed-width').join(' ');
    } else if (pathname.includes('/setup/territory')) {
        body.className = baseBodyClasses.concat('list', 'territories').join(' ');
    } else if (pathname.includes('/setup/quota')) {
        body.className = baseBodyClasses.concat('list', 'fixed-width').join(' ');
    } else if (pathname.includes('/setup/activity-quota')) {
        body.className = baseBodyClasses.concat('list', 'fixed-width').join(' ');
    } else if (pathname.includes('/setup/api-key')) {
        body.className = baseBodyClasses.concat('list').join(' ');
    } else if (pathname.includes('/setup/mobile')) {
        body.className = baseBodyClasses.concat('list', 'fixed-width').join(' ');
    } else if (pathname.includes('/setup/destination')) {
        body.className = baseBodyClasses.concat('list').join(' ');
    } else if (pathname.includes('/setup/unbounce')) {
        body.className = baseBodyClasses.concat('list', 'unbounce-pages').join(' ');
    } else if (pathname.includes('/setup/sender-address')) {
        body.className = baseBodyClasses.concat('list').join(' ');
    } else if (pathname.includes('/setup/email-security')) {
        body.className = baseBodyClasses.concat('list').join(' ');
    } else if (pathname.includes('/setup/zapier')) {
        body.className = baseBodyClasses.concat('list').join(' ');
    } else if (
        // TODO, make this a bit more robust
        !pathname.includes('/setup/user') &&
        !pathname.includes('/setup/billing') &&
        !pathname.includes('/setup/import')
    ) {
        // Fall back only if setting body classes is not handled by another root component
        body.className = baseBodyClasses.join(' ');
    }
}

function getPageTitles(pathname: string): TitleBarProps | null | undefined {
    if (pathname.includes('/setup/index')) {
        return {
            pageTitle: 'Setup',
            pageSubTitle: 'Only admins can view this page',
        };
    }

    if (pathname.includes('/setup/user/settings')) {
        return null;
    }
    if (pathname.includes('/setup/user/email')) {
        return null;
    }
    if (pathname.includes('/setup/user/notifications')) {
        return null;
    }

    if (pathname.includes('/setup/user')) {
        return {
            pageTitle: 'Users and teams',
            pageSubTitle:
                'Invite or remove your colleagues, create and edit teams, and review your team’s account settings.',
        };
    }

    if (pathname.includes('/setup/email-security')) {
        return {
            pageTitle: 'Email security',
            pageSubTitle:
                'Add email addresses to this blocklist to keep their correspondence out of Nutshell',
        };
    }

    if (pathname.includes('/setup/domains')) {
        return {
            pageTitle: 'Domains',
            pageSubTitle:
                'Add and set up a custom domain. This not only improves brand recognition, but also enhances email deliverability and snippet features.',
        };
    }

    if (pathname.includes('/setup/tracking-snippet')) {
        return {
            pageTitle: 'Tracking snippet',
            pageSubTitle:
                'Add a piece of code to your website to see who’s visiting, how they found you, and what they did before becoming a lead.',
        };
    }

    if (pathname.includes('/setup/sender-address') && NutClientConfig.hasCustomDomains) {
        return {
            pageTitle: 'Sender addresses',
            pageSubTitle: 'Use these email addresses to send outreach to your contacts',
        };
    }

    if (pathname.includes('/setup/security')) {
        return {
            pageTitle: 'Security',
            pageSubTitle: 'Only admins can view this page',
        };
    }

    if (pathname.includes('/setup/billing/pause') || pathname.includes('/setup/billing/restore')) {
        return {
            pageTitle: 'Billing',
            pageSubTitle:
                'Update your Nutshell plan, change your payment method, and download invoices.',
        };
    }

    if (pathname.includes('/setup/billing')) {
        return null;
    }

    if (pathname.includes('/setup/sales-automation')) {
        return null;
    }

    if (pathname.includes('/setup/outcome')) {
        return {
            pageTitle: 'Outcomes',
            pageSubTitle: 'Specify why your leads close, and dig in deeper with our losses report.',
        };
    }

    if (pathname.includes('/setup/process-builder')) {
        return {
            pageTitle: 'Sales processes',
            pageSubTitle: '',
        };
    }

    if (pathname.includes('/setup/distribution/user')) {
        return {
            pageTitle: 'Lead distribution',
            pageSubTitle: 'Tell Nutshell how your leads should be assigned to your team.',
        };
    }

    if (pathname.includes('/setup/distribution')) {
        return {
            pageTitle: 'Triggers and overlays',
            pageSubTitle:
                'Tell Nutshell when leads should be automatically put into a given pipeline.',
        };
    }

    if (pathname.includes('/setup/delay')) {
        return {
            pageTitle: 'Step delays',
            pageSubTitle: '',
        };
    }

    if (pathname.includes('/setup/stage')) {
        return {
            pageTitle: 'Pipelines and stages',
            pageSubTitle: '',
        };
    }

    if (pathname.includes('/setup/activity-type')) {
        return {
            pageTitle: 'Activity types',
            pageSubTitle:
                'Customize the activities that your team uses to communicate with contacts',
        };
    }

    if (pathname.includes('/setup/company-type')) {
        return {
            pageTitle: 'Company types',
            pageSubTitle:
                'Categorize the companies you work with, i.e. “Partner,” “Vendor,” “Potential customer”',
        };
    }

    if (pathname.includes('/setup/competitor')) {
        return {
            pageTitle: 'Competitors',
            pageSubTitle: 'Track your competitors on each lead to compare win and loss rates.',
        };
    }

    if (pathname.includes('/setup/custom-field')) {
        return {
            pageTitle: 'Custom fields',
            pageSubTitle:
                'Customize the information your team collects about companies, people, and leads.',
            limits: <TitleBarLimits type='CUSTOM_FIELDS' />,
        };
    }

    if (pathname.includes('/setup/quote-template')) {
        return {
            pageTitle: 'Quote template',
            pageSubTitle: 'Set up your default information for faster and efficient quotes.',
        };
    }

    if (pathname.includes('/setup/industry')) {
        return {
            pageTitle: 'Industries',
            pageSubTitle: 'Organize your companies by their industry.',
        };
    }

    if (pathname.includes('/setup/market')) {
        if (!NutClientConfig.hasMultipleMarkets) {
            return {
                pageTitle: 'Markets',
                pageSubTitle: 'Choose what currencies are available in your account.',
            };
        }

        return {
            pageTitle: 'Markets',
            pageSubTitle: 'Choose what currencies are available in your account.',
            limits: <TitleBarLimits type='MARKETS' />,
        };
    }

    if (pathname.includes('/setup/product')) {
        return {
            pageTitle: 'Products',
            pageSubTitle: 'Track products sold, with separate pricing per market.',
            buttons: <ProductsPageActionBttns />,
        };
    }

    if (pathname.includes('/setup/source')) {
        return {
            pageTitle: 'Channels & sources',
            pageSubTitle: <SourcesPageDescription />,
            buttons: <AnalyticsStatusButton pageType='sources' includeSeeAll={true} />,
        };
    }

    if (pathname.includes('/setup/tag')) {
        return {
            pageTitle: 'Tags',
            pageSubTitle: 'Create tags to organize companies, people, and leads into groups.',
        };
    }

    if (pathname.includes('/setup/territory')) {
        if (NutClientConfig.hasTerritories) {
            return {
                pageTitle: 'Territories',
                pageSubTitle: 'Specify the regions where your leads are located.',
                limits: <TitleBarLimits type='TERRITORIES' />,
            };
        }

        return {
            pageTitle: 'Territories',
            pageSubTitle: 'Specify the regions where your leads are located.',
        };
    }

    if (pathname.includes('/setup/quota')) {
        return {
            pageTitle: 'Sales quotas',
            pageSubTitle:
                'Distribute your sales quotas throughout the year and amongst your team members.',
        };
    }

    if (pathname.includes('/setup/activity-quota')) {
        return {
            pageTitle: 'Activity quotas',
            pageSubTitle: 'Let your team members know what activity effort is expected of them.',
        };
    }

    if (pathname.includes('/setup/import')) {
        const isUnsubscribe = window.location.search.includes('unsubscribe=1');
        const pageTitle = isUnsubscribe ? 'Import an unsubscribe list' : 'Import your data';
        const pageSubTitle = isUnsubscribe
            ? 'All updated people will be unsubscribed.'
            : 'Upload your existing book of business to Nutshell.';

        return {
            pageTitle: pageTitle,
            pageSubTitle: pageSubTitle,
            buttons: <IntercomLaunchButton>Get help from our team</IntercomLaunchButton>,
        };
    }

    if (pathname.includes('/setup/export')) {
        return {
            pageTitle: 'Exports',
            pageSubTitle: 'Create a backup of all of your data.',
        };
    }

    if (pathname.includes('/setup/people-iq')) {
        return null;
    }

    if (pathname.startsWith('/trash')) {
        return null;
    }

    if (pathname.includes('/setup/integration')) {
        return null;
    }

    if (pathname.includes('/setup/destination')) {
        return {
            pageTitle: 'Destinations',
            pageSubTitle: 'Define other services to open Nutshell leads.',
        };
    }

    if (pathname.includes('/setup/api-key')) {
        return {
            pageTitle: 'API Keys',
            pageSubTitle:
                'Generate secure keys for other applications to access your Nutshell data.',
            buttons: NutClientConfig.hasApiSupport ? <GetAPISupport /> : undefined,
        };
    }

    if (pathname.includes('/setup/mobile')) {
        return {
            pageTitle: 'Mobile devices',
            pageSubTitle:
                'See who has accessed Nutshell from a mobile device and remove access for certain devices.',
        };
    }

    if (pathname.includes('/setup/unbounce')) {
        return {
            pageTitle: 'Unbounce pages',
            pageSubTitle:
                'Connect pages to Nutshell and automatically create a lead for each form submission.',
        };
    }

    if (pathname.includes('/setup/sql-access')) {
        return {
            pageTitle: 'SQL access',
            pageSubTitle: 'Gain access to SQL for building out custom integrations',
        };
    }

    if (pathname.includes('/setup/app-connect')) {
        return {
            pageTitle: 'AppConnect',
            pageSubTitle: 'Let Nutshell setup, monitor, and maintain your integrations for you',
        };
    }

    if (pathname.includes('/setup/custom-integration')) {
        return {
            pageTitle: 'Custom integration quote',
            pageSubTitle:
                'Nutshell’s team can work with you to craft specific integrations. With custom development, no integration is impossible.',
        };
    }

    if (pathname.includes('/setup/constant-contact')) {
        return {
            pageTitle: 'Constant Contact',
            pageSubTitle:
                'Connect with Nutshell to keep your email marketing in sync with the rest of your business.',
        };
    }

    if (pathname.includes('/setup/sender-address')) {
        return {
            pageTitle: 'Sender addresses',
            pageSubTitle: 'Use these email addresses to send outreach to your contacts',
        };
    }
    if (pathname.includes('/setup/zapier')) {
        return {
            pageTitle: 'Zapier',
            pageSubTitle: (
                <span>
                    Zapier is a third-party service that you can use to connect Nutshell with other
                    tools in your business.{' '}
                    <Link
                        variant='primary'
                        newTab={true}
                        href='https://support.nutshell.com/en/articles/8429065-integrations-connecting-nutshell-with-zapier'
                    >
                        Learn more in our knowledge base.
                    </Link>
                </span>
            ),
        };
    }

    if (pathname.includes('/setup/audit-log')) {
        return {
            pageTitle: 'Audit log',
            pageSubTitle: 'Admin-only log of bulk edits, user logins, and list exports',
        };
    }

    if (pathname.includes('/setup/ai-features')) {
        return {
            pageTitle: 'AI features',
            pageSubTitle: NutClientConfig.hasAiOutcomesFF
                ? 'Set permissions and monitor usage of Nutshell’s AI features for your team'
                : 'Set permissions and monitor usage of Nutshell’s core AI features',
        };
    }

    return {
        pageTitle: 'Settings',
        pageSubTitle: '',
    };
}

// @ts-expect-error
export const SetupRoot = withRouter<Record<any, any>>(SetupRootComponent);
